import { normalizeCountryIso, getCountry } from '../data/countries';
import { findPropertyById } from './properties';

const getInvestmentCostUsd = (inv) => {
  const n = Number(inv?.invested ?? inv?.totalCostBasisUsd ?? inv?.totalUsd ?? 0);
  return Number.isFinite(n) ? n : 0;
};

/** Share subtotal at purchase — excludes acquisition fee and VAT. */
const getInvestmentSubtotalUsd = (inv) => {
  const stored = Number(inv?.subtotalUsd);
  if (Number.isFinite(stored) && stored >= 0) return stored;
  const invested = getInvestmentCostUsd(inv);
  const acq = Number(inv?.acquisitionFeeUsd) || 0;
  const vat = Number(inv?.vatAmountUsd) || 0;
  return Math.round(Math.max(0, invested - acq - vat) * 100) / 100;
};

/** Until live NAV exists, current value equals recorded cost basis (no markup). */
const getInvestmentCurrentValueUsd = (inv) => getInvestmentCostUsd(inv);

const getInvestmentCostPerShareUsd = (inv) => {
  const shares = Number(inv?.shares) || 0;
  if (shares <= 0) return 0;
  return getInvestmentCostUsd(inv) / shares;
};

const getInvestmentSubtotalPerShareUsd = (inv) => {
  const shares = Number(inv?.shares) || 0;
  if (shares <= 0) return 0;
  return getInvestmentSubtotalUsd(inv) / shares;
};

const getPrimaryReturnBreakdown = (investment, sharesToSell) => {
  const sharesHeld = Number(investment?.shares) || 0;
  if (sharesHeld <= 0 || sharesToSell <= 0) {
    return {
      subtotalRefundUsd: 0,
      acquisitionFeeRetainedUsd: 0,
      vatRetainedUsd: 0,
      managementFeeUsd: 0,
      netProceedsUsd: 0
    };
  }
  const ratio = sharesToSell / sharesHeld;
  const subtotalUsd = getInvestmentSubtotalUsd(investment);
  const acquisitionFeeUsd = Number(investment?.acquisitionFeeUsd) || 0;
  const vatAmountUsd = Number(investment?.vatAmountUsd) || 0;
  return {
    subtotalRefundUsd: Math.round(subtotalUsd * ratio * 100) / 100,
    acquisitionFeeRetainedUsd: Math.round(acquisitionFeeUsd * ratio * 100) / 100,
    vatRetainedUsd: Math.round(vatAmountUsd * ratio * 100) / 100,
  };
};

const sumWalletDividendsUsd = (transactions) => {
  if (!Array.isArray(transactions)) return 0;
  return transactions.reduce((sum, tx) => {
    const type = String(tx?.type || "").toLowerCase();
    if (type !== "dividend" && type !== "distribution" && type !== "rental_payout") return sum;
    const amt = Number(tx?.amountUsd ?? tx?.amount ?? 0);
    return sum + (Number.isFinite(amt) ? amt : 0);
  }, 0);
};

const getSharesOwnedForProperty = (investments, propertyId) => {
  if (!Array.isArray(investments) || propertyId == null) return 0;
  return investments
    .filter((inv) => {
      if (String(inv.propertyId) !== String(propertyId)) return false;
      const status = String(inv.status || "active").toLowerCase();
      return status === "active";
    })
    .reduce((sum, inv) => sum + (Number(inv.shares) || 0), 0);
};

const formatOwnedSharesMessage = (shares) => {
  const n = Number(shares) || 0;
  if (n <= 0) return "";
  const label = n === 1 ? "1 share" : `${n.toLocaleString()} shares`;
  return `You already own ${label} of this property.`;
};

const resolveInvestmentProperty = (properties, inv) => {
  if (!inv) return null;
  const fromList = findPropertyById(properties, inv.propertyId);
  if (fromList) return fromList;
  if (inv.property && (inv.property.id || inv.propertyId)) {
    return { id: inv.property.id || inv.propertyId, ...inv.property };
  }
  return null;
};

/** Countries/markets where the user holds properties (portfolio stat help copy). */
const getPortfolioGeographySummary = (investments, properties) => {
  const countryCodes = new Set();
  const propertyIds = new Set();
  let totalShares = 0;

  investments.forEach((inv) => {
    if (inv.propertyId != null) propertyIds.add(String(inv.propertyId));
    totalShares += Number(inv.shares) || 0;
    const p = resolveInvestmentProperty(properties, inv);
    if (!p) return;
    const code = normalizeCountryIso(p.country || "");
    if (code) countryCodes.add(code);
  });

  const propertyCount = propertyIds.size;
  const countryCount = countryCodes.size;
  const countryNames = [...countryCodes].map((c) => getCountry(c).name);
  const sharesLabel = totalShares === 1 ? "1 share" : `${totalShares.toLocaleString()} shares`;

  let help = "Number of distinct properties in your portfolio.";
  if (propertyCount === 0) {
    help =
      "When you invest, each property is tagged with a listing country (e.g. United States, Nigeria, United Kingdom).";
  } else if (propertyCount === 1 && countryCount <= 1) {
    help = countryNames[0]
      ? `You hold ${sharesLabel} in 1 property listed in ${countryNames[0]}.`
      : `You hold ${sharesLabel} in 1 property.`;
  } else if (countryCount <= 1) {
    help = countryNames[0]
      ? `You hold ${sharesLabel} across ${propertyCount} properties, all listed in ${countryNames[0]}.`
      : `You hold ${sharesLabel} across ${propertyCount} properties.`;
  } else {
    help = `You hold ${sharesLabel} across ${propertyCount} properties in ${countryCount} countries: ${countryNames.join(", ")}.`;
  }

  return { propertyCount, totalShares, countryCount, countryNames, help };
};

/** One portfolio table row per property (sums shares and cost across multiple purchase lots). */
const groupPortfolioHoldings = (holdings, properties) => {
  if (!Array.isArray(holdings) || holdings.length === 0) return [];

  const byProperty = new Map();
  for (const inv of holdings) {
    const pid = String(inv.propertyId ?? '');
    if (!pid) continue;
    if (!byProperty.has(pid)) {
      byProperty.set(pid, {
        propertyId: pid,
        investments: [],
        shares: 0,
        investedUsd: 0,
        currentValueUsd: 0
      });
    }
    const row = byProperty.get(pid);
    row.investments.push(inv);
    row.shares += Number(inv.shares) || 0;
    row.investedUsd += getInvestmentCostUsd(inv);
    row.currentValueUsd += getInvestmentCurrentValueUsd(inv);
  }

  return Array.from(byProperty.values())
    .map((row) => {
      const first = row.investments[0];
      const p =
        resolveInvestmentProperty(properties, first)
        || (first?.property
          ? { id: row.propertyId, ...first.property }
          : {
              id: row.propertyId,
              title: 'Property',
              location: '—',
              shares: 0,
              price: 0
            });
      return {
        ...row,
        p,
        lotCount: row.investments.length,
        investedUsd: Math.round(row.investedUsd * 100) / 100,
        currentValueUsd: Math.round(row.currentValueUsd * 100) / 100
      };
    })
    .sort((a, b) => String(a.p?.title || '').localeCompare(String(b.p?.title || '')));
};

/** Sell modal + API: multiple purchase lots for one property. */
const getInvestmentLots = (investmentOrGroup) => {
  if (Array.isArray(investmentOrGroup?.lots) && investmentOrGroup.lots.length) {
    return investmentOrGroup.lots.filter((lot) => lot?.id);
  }
  return investmentOrGroup?.id ? [investmentOrGroup] : [];
};

const getTotalSharesForLots = (lots) =>
  lots.reduce((sum, lot) => sum + (Number(lot.shares) || 0), 0);

const getWeightedCostPerShareUsd = (lots) => {
  const totalShares = getTotalSharesForLots(lots);
  if (totalShares <= 0) return 0;
  const totalCost = lots.reduce((sum, lot) => sum + getInvestmentCostUsd(lot), 0);
  return totalCost / totalShares;
};

const getWeightedSubtotalPerShareUsd = (lots) => {
  const totalShares = getTotalSharesForLots(lots);
  if (totalShares <= 0) return 0;
  const totalSubtotal = lots.reduce((sum, lot) => sum + getInvestmentSubtotalUsd(lot), 0);
  return totalSubtotal / totalShares;
};

const getAggregatedPrimaryReturnBreakdown = (lots, sharesToSell) => {
  let remaining = sharesToSell;
  let subtotalRefundUsd = 0;
  let acquisitionFeeRetainedUsd = 0;
  let vatRetainedUsd = 0;

  for (const lot of lots) {
    if (remaining <= 0) break;
    const fromLot = Math.min(remaining, Number(lot.shares) || 0);
    if (fromLot <= 0) continue;
    const part = getPrimaryReturnBreakdown(lot, fromLot);
    subtotalRefundUsd += part.subtotalRefundUsd;
    acquisitionFeeRetainedUsd += part.acquisitionFeeRetainedUsd;
    vatRetainedUsd += part.vatRetainedUsd;
    remaining -= fromLot;
  }

  return {
    subtotalRefundUsd: Math.round(subtotalRefundUsd * 100) / 100,
    acquisitionFeeRetainedUsd: Math.round(acquisitionFeeRetainedUsd * 100) / 100,
    vatRetainedUsd: Math.round(vatRetainedUsd * 100) / 100
  };
};

const isMarketplaceAcquiredLot = (lot) =>
  String(lot?.acquiredVia || "").toLowerCase() === "marketplace";

const isPropertyFullyFunded = (property) => {
  if (!property) return false;
  if (String(property.status || "").toLowerCase() === "funded") return true;
  const total = Number(property.shares) || 0;
  const sold = Number(property.sharesSold) || 0;
  return total > 0 && sold >= total;
};

/** Primary pool return only for shares bought in the offering while it is still open. */
const shouldReturnSharesToPrimaryOffering = (property, lots) => {
  const activeLots = Array.isArray(lots) ? lots.filter((lot) => (Number(lot?.shares) || 0) > 0) : [];
  if (!activeLots.length) return false;
  if (isPropertyFullyFunded(property)) return false;
  if (String(property?.status || "").toLowerCase() !== "funding") return false;
  if (activeLots.some(isMarketplaceAcquiredLot)) return false;
  return true;
};

export {
  getInvestmentCostUsd,
  getInvestmentSubtotalUsd,
  getInvestmentCurrentValueUsd,
  getInvestmentCostPerShareUsd,
  getInvestmentSubtotalPerShareUsd,
  getPrimaryReturnBreakdown,
  sumWalletDividendsUsd,
  getSharesOwnedForProperty,
  formatOwnedSharesMessage,
  resolveInvestmentProperty,
  getPortfolioGeographySummary,
  groupPortfolioHoldings,
  getInvestmentLots,
  getTotalSharesForLots,
  getWeightedCostPerShareUsd,
  getWeightedSubtotalPerShareUsd,
  getAggregatedPrimaryReturnBreakdown,
  isMarketplaceAcquiredLot,
  isPropertyFullyFunded,
  shouldReturnSharesToPrimaryOffering,
};
